import { useState, useRef } from 'react';
import axios from 'axios';
import { Lifebuoy, Upload, FileZip, DownloadSimple, Warning, CheckCircle, XCircle, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { todayJst } from '@/utils/jstDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 緊急復旧カード — Emergency Restore.
 *
 * When the NAS is unreachable but the admin has the file bytes on their local
 * disk (from an SMB copy, a backup drive, whatever), this tool re-uploads
 * those files to Emergent Cloud and flips the DB pointer so the app starts
 * serving them again.
 *
 * Three intake modes:
 *   1. Folder picker (webkitdirectory) — recursively pick a folder
 *   2. Multi-file drag-drop — drag files from Finder
 *   3. Single ZIP upload — server-side extraction
 *
 * Per user preference: STOP on first failure. Show a clear error and let
 * the admin decide what to do (rename? delete? retry?).
 */
export function EmergencyRestoreCard() {
  const folderRef = useRef(null);
  const zipRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, matched: 0 });
  const [logLines, setLogLines] = useState([]);
  const [stopped, setStopped] = useState(null); // { filename, reason }

  const reset = () => {
    setProgress({ done: 0, total: 0, matched: 0 });
    setLogLines([]);
    setStopped(null);
  };

  const downloadManifest = async () => {
    try {
      const r = await axios.get(`${API}/storage/emergency-restore/manifest.csv`, {
        responseType: 'blob', withCredentials: true,
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `nas-manifest-${todayJst()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('マニフェストをダウンロードしました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'マニフェストの取得に失敗しました');
    }
  };

  const processFiles = async (files) => {
    if (!files || files.length === 0) return;
    reset();
    setRunning(true);
    setProgress({ done: 0, total: files.length, matched: 0 });
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const filename = f.name || (f.webkitRelativePath || '').split('/').pop() || 'unknown';
      try {
        const fd = new FormData();
        fd.append('file', f, filename);
        const r = await axios.post(`${API}/storage/emergency-restore/upload`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = r.data || {};
        setProgress((p) => ({
          done: p.done + 1,
          total: p.total,
          matched: p.matched + (data.matched ? 1 : 0),
        }));
        if (!data.matched) {
          // Per user pref — stop on first non-match
          setStopped({ filename, reason: data.reason || '照合失敗' });
          setLogLines((prev) => [...prev, {
            filename, ok: false, reason: data.reason || '照合失敗',
          }]);
          toast.error(`停止しました: ${filename} — ${data.reason}`);
          break;
        }
        setLogLines((prev) => [...prev, {
          filename, ok: true, app: data.app,
          collection: data.collection, size: data.size,
        }]);
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || 'アップロード失敗';
        setStopped({ filename, reason: msg });
        setLogLines((prev) => [...prev, { filename, ok: false, reason: msg }]);
        toast.error(`エラーで停止: ${filename} — ${msg}`);
        break;
      }
    }
    setRunning(false);
  };

  const processZip = async (zipFile) => {
    if (!zipFile) return;
    reset();
    setRunning(true);
    setProgress({ done: 0, total: 1, matched: 0 });
    try {
      const fd = new FormData();
      fd.append('file', zipFile, zipFile.name);
      const r = await axios.post(`${API}/storage/emergency-restore/upload-zip`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 min — large ZIPs may take a while server-side
      });
      const data = r.data || {};
      setProgress({
        done: data.total_entries || 0,
        total: data.total_entries || 0,
        matched: data.matched || 0,
      });
      const lines = (data.results || []).map((res) => ({
        filename: res.filename || '(unknown)',
        ok: !!res.matched,
        reason: res.reason,
        app: res.app,
        collection: res.collection,
        size: res.size,
      }));
      if (data.stopped_at) {
        lines.push({
          filename: data.stopped_at,
          ok: false,
          reason: data.last_error || '停止',
        });
        setStopped({ filename: data.stopped_at, reason: data.last_error || '停止' });
        toast.error(`ZIP 処理を停止: ${data.stopped_at} — ${data.last_error}`);
      } else {
        toast.success(`ZIP 処理完了: ${data.matched} / ${data.total_entries} 復旧しました`);
      }
      setLogLines(lines);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'ZIP アップロード失敗';
      toast.error(msg);
      setStopped({ filename: zipFile.name, reason: msg });
    } finally {
      setRunning(false);
    }
  };

  const onFolderChosen = (e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    if (folderRef.current) folderRef.current.value = '';
  };

  const onZipChosen = (e) => {
    const f = (e.target.files || [])[0];
    processZip(f);
    if (zipRef.current) zipRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (running) return;
    const items = e.dataTransfer.files;
    if (!items || items.length === 0) return;
    // If exactly one file and it's a .zip → treat as ZIP upload
    if (items.length === 1 && (items[0].name || '').toLowerCase().endsWith('.zip')) {
      processZip(items[0]);
    } else {
      processFiles(Array.from(items));
    }
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="storage-emergency-restore-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-rose-700 shrink-0">
          <Lifebuoy size={20} weight="duotone" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-zinc-900">緊急復旧 — NAS が使えない場合</h2>
          <p className="text-[12px] text-zinc-500 mt-0.5">
            バックアップから直接ファイルをアップロードして、NAS を経由せずに Emergent クラウドに復元します。ファイル名の UUID で自動照合されます。
          </p>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={downloadManifest}
          data-testid="restore-download-manifest-btn"
          className="shrink-0"
        >
          <DownloadSimple size={14} className="mr-1" />
          マニフェスト
        </Button>
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800 flex items-start gap-2">
          <Warning size={16} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <b>NAS の接続確認 OK なら通常の「Emergent クラウドへコピー」ボタンを使ってください。</b>
            この画面は NAS が壊れた・電源が入らない等、通常コピーが使えない緊急時のみに使用します。
          </div>
        </div>

        {/* Drag-drop / picker area */}
        <div
          onDragOver={(e) => { e.preventDefault(); if (!running) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            dragOver
              ? 'border-teal-700 bg-blue-50/50'
              : running
                ? 'border-zinc-300 bg-zinc-50 opacity-60'
                : 'border-zinc-300 hover:border-zinc-400'
          }`}
          data-testid="restore-dropzone"
        >
          <Upload size={36} weight="duotone" className="mx-auto text-zinc-400 mb-2" />
          <div className="text-[13px] text-zinc-700 font-semibold">
            ファイルをドラッグ&amp;ドロップ
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            単一ファイル・複数ファイル・フォルダー・ZIP のいずれも対応
          </div>
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            <Button
              onClick={() => folderRef.current?.click()}
              disabled={running}
              variant="outline" size="sm"
              data-testid="restore-pick-folder-btn"
            >
              <Upload size={13} className="mr-1" />
              フォルダーを選択
            </Button>
            <Button
              onClick={() => zipRef.current?.click()}
              disabled={running}
              variant="outline" size="sm"
              data-testid="restore-pick-zip-btn"
            >
              <FileZip size={13} className="mr-1" />
              ZIP を選択
            </Button>
          </div>
          <input
            ref={folderRef}
            type="file"
            multiple
            webkitdirectory=""
            directory=""
            onChange={onFolderChosen}
            className="hidden"
            data-testid="restore-folder-input"
          />
          <input
            ref={zipRef}
            type="file"
            accept=".zip,application/zip"
            onChange={onZipChosen}
            className="hidden"
            data-testid="restore-zip-input"
          />
        </div>

        {/* Progress + result log */}
        {(progress.total > 0 || logLines.length > 0) && (
          <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="restore-progress-panel">
            <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center gap-3 text-[12px]">
              <span className="font-semibold text-zinc-800">
                {running ? '処理中…' : (stopped ? '停止しました' : '完了')}
              </span>
              <span className="text-zinc-600">
                {progress.done} / {progress.total} 処理
              </span>
              <span className="text-emerald-700">
                <CheckCircle size={12} weight="fill" className="inline mr-0.5 -mt-0.5" />
                {progress.matched} 照合
              </span>
              {stopped && (
                <span className="text-rose-700">
                  <XCircle size={12} weight="fill" className="inline mr-0.5 -mt-0.5" />
                  停止: {stopped.filename}
                </span>
              )}
            </div>
            <div className="max-h-[240px] overflow-y-auto text-[11px] font-mono divide-y divide-zinc-200">
              {logLines.slice(-100).map((line, i) => (
                <div
                  key={i}
                  className={`px-3 py-1.5 flex items-center gap-2 ${line.ok ? 'text-emerald-800' : 'text-rose-800 bg-rose-50/40'}`}
                >
                  {line.ok
                    ? <CheckCircle size={12} weight="fill" className="text-emerald-600 shrink-0" />
                    : <XCircle size={12} weight="fill" className="text-rose-600 shrink-0" />
                  }
                  <span className="truncate flex-1">{line.filename}</span>
                  {line.ok && line.app && (
                    <>
                      <ArrowRight size={11} className="text-zinc-400" />
                      <span className="text-zinc-600">{line.app}</span>
                      {line.size && <span className="text-zinc-400">({(line.size / 1024).toFixed(0)}KB)</span>}
                    </>
                  )}
                  {!line.ok && (
                    <span className="text-rose-700 text-right max-w-[50%] truncate" title={line.reason}>
                      {line.reason}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-zinc-500">
          ※ アップロードされたファイルは Emergent クラウドに保存され、該当 DB レコードの storage_backend が <code>nas</code> → <code>emergent</code> に切り替わります。ダウンロード URL は変わらないため、既存の埋め込みリンクは自動的に新しいバックエンドから配信されます。
          <br />
          ※ 照合キー: ファイル名に含まれる UUID (例: <code>abc12345-6789-...jpg</code>)。フォルダー構造は無視されます。
        </p>
      </div>
    </div>
  );
}

export default EmergencyRestoreCard;
